<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps<{
  question: string;
}>();

const isExpanded = ref(false);
const needsExpansion = ref(false);
const contentRef = ref<HTMLElement | null>(null);
let resizeObserver: ResizeObserver | null = null;

const checkIfNeedsExpansion = () => {
  const element = contentRef.value;
  // 展開中は line-clamp が外れて scrollHeight と clientHeight が一致するため、判定しない
  if (!element || isExpanded.value) return;
  needsExpansion.value = element.scrollHeight > element.clientHeight + 1;
};

onMounted(() => {
  if (!contentRef.value) return;
  resizeObserver = new ResizeObserver(checkIfNeedsExpansion);
  resizeObserver.observe(contentRef.value);
});

onUnmounted(() => {
  resizeObserver?.disconnect();
});

watch(
  () => props.question,
  async () => {
    isExpanded.value = false;
    await nextTick();
    checkIfNeedsExpansion();
  },
);

// 展開中にリサイズで5行以内に収まると、折りたたんでも高さが変わらず ResizeObserver が発火しないため、
// 折りたたみ時にも判定し直す。
watch(isExpanded, async () => {
  await nextTick();
  checkIfNeedsExpansion();
});
</script>

<template>
  <div class="survey-question">
    <h2
      ref="contentRef"
      class="c-title-bold--xm question"
      :class="{ collapsed: !isExpanded }"
    >
      {{ props.question }}
    </h2>
    <div v-if="needsExpansion" class="toggle-row">
      <button
        type="button"
        class="c-btn c-btn--auto c-btn--small c-btnOutline toggle-button"
        @click="isExpanded = !isExpanded"
      >
        <span class="c-title-bold--s">{{
          isExpanded ? '一部を表示' : '全文を表示'
        }}</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.question {
  white-space: pre-line;
  overflow-wrap: anywhere;

  &.collapsed {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
    line-clamp: 5;
  }
}

.toggle-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.toggle-button {
  border: none;
  cursor: pointer;
  height: 24px;
  padding: 0 4px;
  border-radius: 4px;
  background-color: transparent;
  color: $color-gray1000;

  &:hover {
    background-color: $color-gray400;
  }
}
</style>
